import {
  getDistrictMapOptions,
  getInvoiceDetailOptions,
  getRequirementFulfillmentOptions,
  getRequirementSubintervalsOptions,
  populateIntervalMutation,
} from '@/api/main'
import type { DriveType, RequirementFulfillment } from '@/api/main/types.gen'
import { invalidateSchedulerQueries } from '@/routes/scheduling.$startDate.$endDate.drive.$action.$id'
import { Alert, Button, Checkbox, Group, Stack } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TbAlertTriangle } from 'react-icons/tb'
import { AutoScheduleSplit } from './AutoScheduleSplit'
import { ConfirmSummary } from './ConfirmSummary'
import { DistrictPolygonsMap } from './DistrictPolygonsMap'
import { RequirementsTable } from './RequirementsTable'
import { autoScheduleConfirmZIndex } from './autoScheduleLayers'
import { requirementsConfirmLines } from '@/utils/autoScheduleConfirm'
import { retainVisibleDistrictIds, toggleDistrictId } from '@/utils/autoScheduleDistricts'
import {
  defaultRequirementsSnapshot,
  hasRequirementsPayload,
  isRequirementsDirty,
  shouldConfirmSubmit,
} from '@/utils/autoScheduleDirty'
import type { TimeInterval } from '@/utils/autoScheduleIntervals'
import {
  availableVehiclesInWindow,
  driveTypeKey,
  driveTypeLabel,
  drivesInExactInterval,
  partitionRequirementsForInterval,
  uniqueDriveTypeCombinations,
  type DriveTypeCombination,
} from '@/utils/autoScheduleRequirements'

const EMPTY_FULFILLMENT: RequirementFulfillment[] = []

export function RequirementsMode({
  billingPeriodId,
  vehicleId,
  viewStart,
  viewEnd,
  interval,
  onDirtyChange,
  onDriveTypeFilterChange,
  onFinished,
  mapActive = true,
}: {
  billingPeriodId: number
  vehicleId: string
  viewStart: string
  viewEnd: string
  interval: TimeInterval
  onDirtyChange?: (dirty: boolean) => void
  onDriveTypeFilterChange?: (combinations: DriveTypeCombination[]) => void
  onFinished: () => void
  mapActive?: boolean
}) {
  const { t } = useTranslation(['scheduling', 'common'])
  const queryClient = useQueryClient()
  const { data: districts } = useSuspenseQuery(getDistrictMapOptions())
  const { data: subintervals } = useSuspenseQuery(
    getRequirementSubintervalsOptions({
      path: {
        billing_period_id: billingPeriodId,
        starting_from: viewStart,
        ending_at: viewEnd,
      },
    })
  )
  const { data: invoice } = useSuspenseQuery(
    getInvoiceDetailOptions({
      path: { billing_period_id: billingPeriodId },
    })
  )
  const { data: fulfillmentResponse } = useQuery({
    ...getRequirementFulfillmentOptions({
      path: {
        billing_period_id: billingPeriodId,
        timestamp: interval.start,
      },
    }),
    placeholderData: keepPreviousData,
  })

  const requirements = useMemo(() => Object.values(subintervals.requirements), [subintervals.requirements])
  const combinations = useMemo(() => uniqueDriveTypeCombinations(requirements), [requirements])
  const [initial] = useState(() => defaultRequirementsSnapshot(combinations.map(driveTypeKey)))
  const [selectedCombinations, setSelectedCombinations] = useState<DriveTypeCombination[]>(combinations)
  const [selectedDistricts, setSelectedDistricts] = useState<number[]>([])
  const [hoveredDistrict, setHoveredDistrict] = useState<number>()
  const [ignoreCooldown, setIgnoreCooldown] = useState(false)

  const { relevant, irrelevant } = useMemo(
    () =>
      partitionRequirementsForInterval({
        requirements,
        requirementSubintervals: subintervals.requirement_subintervals,
        selectedCombinations,
        intervalStart: interval.start,
        intervalEnd: interval.end,
      }),
    [requirements, subintervals.requirement_subintervals, selectedCombinations, interval]
  )

  const relevantDistricts = useMemo(() => new Set(relevant.map(item => item.district_id)), [relevant])
  const selectedVisibleDistricts = retainVisibleDistrictIds(selectedDistricts, relevantDistricts)
  const mapDistricts = useMemo(
    () => districts.filter(district => relevantDistricts.has(district.id)),
    [districts, relevantDistricts]
  )

  useEffect(() => {
    setSelectedDistricts(current => retainVisibleDistrictIds(current, relevantDistricts))
  }, [relevantDistricts])

  useEffect(() => {
    onDriveTypeFilterChange?.(selectedCombinations)
  }, [selectedCombinations, onDriveTypeFilterChange])

  useEffect(() => {
    onDirtyChange?.(
      isRequirementsDirty(
        {
          districtIds: selectedVisibleDistricts,
          ignoreCooldown,
          combinationKeys: selectedCombinations.map(driveTypeKey),
        },
        initial
      )
    )
  }, [selectedVisibleDistricts, ignoreCooldown, selectedCombinations, initial, onDirtyChange])

  const availableVehicles = availableVehiclesInWindow(subintervals.drive_subintervals, interval.start, interval.end)
  const drives = useMemo(
    () => drivesInExactInterval(subintervals.drive_subintervals, interval.start, interval.end),
    [subintervals.drive_subintervals, interval.start, interval.end]
  )
  const fulfillment = fulfillmentResponse?.fulfillment ?? EMPTY_FULFILLMENT

  const { mutate: populate, isPending } = useMutation({
    ...populateIntervalMutation(),
    onSuccess: data => {
      if (data.scheduled_drives.length === 0) {
        showNotification({ message: t('autoSchedule.noRoutesFound'), color: 'orange' })
        return
      }
      showNotification({
        message: t('autoSchedule.populated', { count: data.scheduled_drives.length }),
        color: 'green',
      })
      invalidateSchedulerQueries(queryClient)
      queryClient.removeQueries({
        predicate: query => {
          const firstKey = query.queryKey[0] as { _id?: string } | undefined
          return firstKey?._id === 'getRequirementFulfillment'
        },
      })
      onFinished()
    },
    onError: () => {
      showNotification({ message: t('autoSchedule.populateFailed'), color: 'red' })
    },
  })

  const toggle = useCallback((districtId: number) => {
    setSelectedDistricts(current => toggleDistrictId(current, districtId))
  }, [])

  const selectedKeys = selectedCombinations.map(driveTypeKey)

  const confirmPopulate = (onlyEvidence: boolean) => {
    if (!shouldConfirmSubmit(hasRequirementsPayload(selectedVisibleDistricts))) {
      return
    }
    const lines = requirementsConfirmLines({
      vehicleId,
      interval,
      districtIds: selectedVisibleDistricts,
      districts,
      driveTypeCombinations: selectedCombinations,
      ignoreCooldown,
      onlyEvidence,
      labels: {
        vehicle: t('autoSchedule.vehicle'),
        interval: t('autoSchedule.interval'),
        districts: t('autoSchedule.districts'),
        driveTypes: t('driveType'),
        ignoreCooldown: t('autoSchedule.ignoreCooldown'),
        evidenceMode: t('autoSchedule.evidenceMode'),
        yes: t('yes', { ns: 'common' }),
        no: t('no', { ns: 'common' }),
        evidenceOnly: t('autoSchedule.evidenceOnly'),
        allDriveTypes: t('autoSchedule.allDriveTypes'),
      },
    })
    modals.openConfirmModal({
      title: onlyEvidence ? t('autoSchedule.populateEvidence') : t('autoSchedule.populate'),
      centered: true,
      zIndex: autoScheduleConfirmZIndex,
      children: <ConfirmSummary lines={lines} />,
      labels: {
        confirm: onlyEvidence ? t('autoSchedule.populateEvidence') : t('autoSchedule.populate'),
        cancel: t('cancel', { ns: 'common' }),
      },
      confirmProps: { color: 'blue' },
      onConfirm: () =>
        populate({
          body: {
            billing_period_id: billingPeriodId,
            vehicle_id: vehicleId,
            district_ids: selectedVisibleDistricts,
            start_after: interval.start,
            finish_before: interval.end,
            ignore_cooldown: ignoreCooldown,
            only_evidence: onlyEvidence,
          },
        }),
    })
  }

  if (!mapActive) {
    return null
  }

  return (
    <AutoScheduleSplit
      map={
        <DistrictPolygonsMap
          key={`${interval.start}:${interval.end}`}
          districts={mapDistricts}
          selectedIds={selectedVisibleDistricts}
          hoveredId={hoveredDistrict}
          onToggle={toggle}
          onHover={setHoveredDistrict}
          drives={drives}
          vehicleId={vehicleId}
        />
      }
      sidebar={
        <>
          <Checkbox.Group
            style={{ flexShrink: 0 }}
            value={selectedKeys}
            onChange={keys =>
              setSelectedCombinations(
                combinations.filter(combination => keys.includes(driveTypeKey(combination))) as DriveType[][]
              )
            }
          >
            <Group gap="sm">
              {combinations.map(combination => (
                <Checkbox
                  key={driveTypeKey(combination)}
                  value={driveTypeKey(combination)}
                  label={driveTypeLabel(combination)}
                />
              ))}
            </Group>
          </Checkbox.Group>
          <RequirementsTable
            relevant={relevant}
            irrelevant={irrelevant}
            fulfillment={fulfillment}
            invoiceRows={invoice.invoice_rows}
            availableVehicles={availableVehicles}
            selectedIds={selectedVisibleDistricts}
            hoveredId={hoveredDistrict}
            onToggle={toggle}
            onHover={setHoveredDistrict}
          />
          <Stack gap="sm" style={{ flexShrink: 0 }}>
            <Checkbox
              label={t('autoSchedule.ignoreCooldown')}
              checked={ignoreCooldown}
              onChange={event => setIgnoreCooldown(event.currentTarget.checked)}
            />
            {ignoreCooldown && (
              <Alert color="red" icon={<TbAlertTriangle />}>
                {t('autoSchedule.ignoreCooldownWarning')}
              </Alert>
            )}
            <Button
              loading={isPending}
              disabled={selectedVisibleDistricts.length === 0}
              onClick={() => confirmPopulate(false)}
            >
              {t('autoSchedule.populate')}
            </Button>
            <Button
              variant="light"
              loading={isPending}
              disabled={selectedVisibleDistricts.length === 0}
              onClick={() => confirmPopulate(true)}
            >
              {t('autoSchedule.populateEvidence')}
            </Button>
          </Stack>
        </>
      }
    />
  )
}
